import React, { useEffect, useState } from 'react';
import './styles.scss';
import { Button, Input, Switch, ToastMessage } from '@cokitchen/cokitchen-components';
import { CloseOutlineIcon } from 'cokitchen-icons';
import { LatLngLiteral, LatLngTuple } from 'leaflet';
import { apiService } from 'services/base.service';
import { toast } from 'react-toastify';
import ReactModal from 'react-modal';
import { isEqual } from 'lodash';
import useDashboardContext from 'contexts/dashboard.context';
import Map from 'components/map';

export type PolygonModalType = 'create' | 'edit';

type FormDataType = {
  name: string;
  delivery_fee: string;
  delivery_service_charge: string;
  cokitchen_polygon_id?: string;
  delivery?: boolean;
  pickup?: boolean;
};

type PolygonModalProps = {
  data?: FormDataType;
  positions?: LatLngLiteral[] | LatLngTuple[];
  type?: PolygonModalType;
  show: boolean;
  onClose: (refetchData?: boolean) => void;
  onEditComplete?: (arg0: any) => void;
};

const PolygonModal = ({
  positions,
  data,
  type = 'create',
  show,
  onClose,
  onEditComplete
}: PolygonModalProps) => {
  const { cokitchenPolygons, setCokitchenPolygons } = useDashboardContext();
  const [loading, setLoading] = React.useState(false);
  const [formData, setFormData] = React.useState({} as FormDataType);
  const [initialData, setInitialData] = React.useState({} as FormDataType);
  const [cords, setCords] = useState<LatLngLiteral[] | LatLngTuple[]>([]);
  const [initialCords, setInitialCords] = useState<LatLngLiteral[] | LatLngTuple[]>([]);

  const handleChange = (e: any) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const getCordinates = (cords: LatLngLiteral[] | LatLngTuple[]) => {
    const coordinates = cords?.map((cord: any) => [cord?.lat, cord?.lng]);
    return coordinates;
  };

  const resetForm = () => {
    setFormData({} as FormDataType);
    setCords([]);
  };

  const getUpdatedFields = () => {
    type Type = FormDataType & {
      polygon: any[][];
    };
    const updatedFields: {
      [key: string]: unknown;
    } = {
      cokitchen_polygon_id: data?.cokitchen_polygon_id
    };

    const initial: Type = {
      ...initialData,
      polygon: getCordinates(initialCords)
    };

    const updated: Type = {
      ...formData,
      polygon: getCordinates(cords)
    };

    Object.entries(updated).map(([key, value]) => {
      if (!isEqual(value, initial[key as keyof Type])) {
        updatedFields[key as keyof Type] = value;
      }
    });

    return updatedFields;
  };

  const createPolygon = async () => {
    setCokitchenPolygons?.([
      ...(cokitchenPolygons ?? []),
      {
        ...formData,
        polygon: getCordinates(cords)
      }
    ]);

    onClose(true);
    setFormData({} as FormDataType);
  };

  const editPolygon = async () => {
    setLoading(true);
    const payload = getUpdatedFields();
    try {
      const res = await apiService.put(`marketing/cokitchen-polygon`, { data: payload });
      if (res?.data) {
        toast(<ToastMessage type='success' message={`${formData?.name} updated successfully`} />);
        resetForm();
        onClose(true);
        onEditComplete?.(payload?.polygon);
      }
    } catch (err: any) {
      toast(<ToastMessage type='error' message={err?.response?.data?.message} />);
    }
    setLoading(false);
  };

  const handleConfirm = () => {
    if (type === 'create') {
      createPolygon();
    } else {
      editPolygon();
    }
  };

  const btnDisabled = () => {
    return (
      !formData?.name ||
      !formData?.delivery_fee ||
      !formData?.delivery_service_charge ||
      cords?.length === 0
    );
  };
  useEffect(() => {
    if (data && type === 'edit') {
      setFormData(data);
      setCords(positions ?? []);
      setInitialData(data);
      setInitialCords(positions ?? []);
    } else {
      resetForm();
    }
  }, [type, data]);

  const customStyles = {
    content: {
      top: '50%',
      left: '50%',
      right: 'auto',
      bottom: 'auto',
      marginRight: '-50%',
      transform: 'translate(-50%, -50%)',
      borderRadius: '10px',
      backgroundColor: '#fff',
      border: 'none',
      padding: '0px',
      overflow: 'unset'
    },
    overlay: {
      background: 'rgba(0, 0, 0, 0.4)',
      backdropFilter: 'blur(3px)',
      zIndex: 10000
    }
  };

  return (
    <div>
      <ReactModal
        isOpen={show}
        style={customStyles}
        onRequestClose={() => onClose()}
        ariaHideApp={false}>
        <div className='polygon-modal'>
          <div className='polygon-modal__content'>
            <div className='polygon-modal__header'>
              <div></div>
              <h4 className='polygon-modal__title'>
                {type === 'create' ? 'Create' : 'Edit'} Polygon
              </h4>
              <div className='flex justify-center items-center rounded-full bg-[#EFF0F0] w-5 h-5'>
                <CloseOutlineIcon className='cursor-pointer ' onClick={() => onClose()} />
              </div>
            </div>

            <div className='polygon-modal__map'>
              <Map type={type} positions={cords} setCords={setCords} />
            </div>
            <div className='polygon-modal__form'>
              <Input
                name='name'
                value={formData?.name}
                onChange={handleChange}
                label='Polygon Name'
                placeholder='Enter Polygon Name'
                inputClass='h-[40px]'
              />
              <div className='flex flex-wrap gap-4 justify-between items-center'>
                <Input
                  name='delivery_fee'
                  value={formData?.delivery_fee}
                  onChange={handleChange}
                  label='Delivery Fee'
                  placeholder='2,000'
                  className='w-full md:w-[45%]'
                  inputClass='h-[40px]'
                />
                <Input
                  name='delivery_service_charge'
                  value={formData?.delivery_service_charge}
                  onChange={handleChange}
                  label='Delivery service Fee'
                  placeholder='2,000'
                  className='w-full md:w-[45%]'
                  inputClass='h-[40px]'
                />
              </div>
              <div className='flex gap-6 mt-2 ml-1'>
                <Switch
                  label='Delivery'
                  active={formData.delivery || false}
                  labelPosition='right'
                  className='mb-2'
                  onChange={(e: any) => {
                    setFormData({ ...formData, delivery: e });
                  }}
                  switchClassName='bg-red-5'
                />
                <Switch
                  label='Pickup'
                  active={formData.pickup || false}
                  labelPosition='right'
                  className='mb-2'
                  onChange={(e: any) => {
                    setFormData({ ...formData, pickup: e });
                  }}
                  switchClassName='bg-red-5'
                />
              </div>
            </div>
            <div className='polygon-modal__btn'>
              <Button
                onClick={handleConfirm}
                loading={loading}
                color='primary'
                disabled={btnDisabled()}
                variant='block'
                small>
                {type === 'create' ? 'Create' : 'Update'} Polygon
              </Button>
            </div>
          </div>
        </div>
      </ReactModal>
    </div>
  );
};

export default PolygonModal;
